// @/utils/markdown_paste_handler.js
import { marked } from 'marked';

// 테스트 환경 감지
const isTestEnv = typeof process !== 'undefined' && process.env?.NODE_ENV === 'test';

// --- 1. Shiki Highlighter를 모듈 스코프에서 비동기적으로 초기화 ---
let highlighter;

// 브라우저 환경에서만 Shiki 초기화 (테스트 환경 제외)
if (typeof window !== 'undefined' && !isTestEnv) {
    (async () => {
        try {
            const { createHighlighter } = await import('shiki');
            const { CODE_LANGUAGES } = await import('@/editorjs/utils');
            // 테마와 언어는 필요한 것만 명시하여 번들 크기를 최적화할 수 있습니다.
            highlighter = await createHighlighter({
                themes: ['github-light', 'github-dark'],
                langs: CODE_LANGUAGES,
            });
        } catch (e) {
            console.error("Failed to initialize Shiki highlighter:", e);
            // highlighter 초기화 실패 시, shiki를 사용하지 않는 대체 로직을 제공할 수도 있습니다.
        }
    })();
}

export function escapeHtml(text) {
    return text.replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// EditorJS 인라인 도구가 사용하는 태그로 변환
// marked는 <strong>, <em>을 생성하지만, EditorJS는 <b>, <i>를 사용함
export function convertToEditorJsTags(html) {
    return html
        .replace(/<strong>/g, '<b>')
        .replace(/<\/strong>/g, '</b>')
        .replace(/<em>/g, '<i>')
        .replace(/<\/em>/g, '</i>');
}

// 마크다운 인라인 문법을 직접 HTML로 변환
// marked.parseInline이 제대로 동작하지 않는 케이스 대응
export function manualInlineMarkdownToHtml(text) {
    if (!text) return text;

    let result = text;

    // 볼드: **text** 또는 __text__ → <b>text</b>
    // 주의: 이탤릭(*text*)보다 먼저 처리해야 함
    result = result.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
    result = result.replace(/__(.+?)__/g, '<b>$1</b>');

    // 이탤릭: *text* 또는 _text_ → <i>text</i>
    // 단, 단어 중간의 언더스코어는 제외 (예: some_variable_name)
    result = result.replace(/(?<!\w)\*([^\*]+)\*(?!\w)/g, '<i>$1</i>');
    result = result.replace(/(?<!\w)_([^_]+)_(?!\w)/g, '<i>$1</i>');

    // 인라인 코드: `code` → <code>code</code>
    result = result.replace(/`([^`]+)`/g, '<code>$1</code>');

    // 링크: [text](url) → <a href="url">text</a>
    result = result.replace(/\[([^\]]+)\]\(([^\)]+)\)/g, '<a href="$2">$1</a>');

    return result;
}

// 인라인 토큰을 HTML로 직접 변환
// marked의 토큰 구조를 직접 HTML로 변환하여 **bold** 같은 마크다운이 정확히 처리되도록 함
export function inlineTokensToHtml(tokens, depth = 0) {
    if (!tokens || tokens.length === 0) return '';

    const result = tokens.map(token => {
        switch (token.type) {
            case 'text':
                // text 토큰 안에 마크다운 문법이 남아있는 경우 (lexer가 인라인 파싱을 안 한 경우)
                // marked.parseInline이 제대로 동작하지 않으므로 직접 변환
                const hasMarkdown = token.raw && (token.raw.includes('**') || token.raw.includes('__') ||
                    token.raw.includes('[') || token.raw.includes('`'));
                if (hasMarkdown) {
                    return manualInlineMarkdownToHtml(token.raw);
                }
                return token.text;
            case 'strong':
                return `<b>${inlineTokensToHtml(token.tokens, depth + 1)}</b>`;
            case 'em':
                return `<i>${inlineTokensToHtml(token.tokens, depth + 1)}</i>`;
            case 'codespan':
                return `<code>${escapeHtml(token.text)}</code>`;
            case 'link':
                const linkText = inlineTokensToHtml(token.tokens, depth + 1);
                return `<a href="${token.href}">${linkText}</a>`;
            case 'br':
                return '<br>';
            case 'del':
                // strikethrough는 EditorJS에서 지원하지 않으므로 텍스트만 반환
                return token.text;
            case 'html':
                return token.raw;
            default:
                // 알 수 없는 타입은 raw 또는 text 반환
                return token.raw || token.text || '';
        }
    }).join('');

    return result;
}

function tokensToEditorBlocks(tokens) {
    const blocks = [];

    // 중첩 리스트를 재귀적으로 처리하는 헬퍼 함수
    const buildListItems = (items) => {
        return items.map(item => {
            // 1. content 추출: 인라인 토큰을 직접 HTML로 변환
            const inlineTokens = item.tokens?.filter(t => t.type !== 'list') || [];
            // paragraph 토큰 내부의 tokens를 추출하여 처리
            const flattenedTokens = inlineTokens.flatMap(t =>
                t.type === 'paragraph' && t.tokens ? t.tokens : [t]
            );
            const content = inlineTokensToHtml(flattenedTokens);

            // 2. 중첩된 items 추출: 모든 'list' 토큰을 찾습니다.
            const nestedListTokens = item.tokens?.filter(t => t.type === 'list');

            let nestedItems = [];
            if (nestedListTokens && nestedListTokens.length > 0) {
                // 3. 각 list 토큰의 items를 재귀적으로 처리하고, 그 결과를 하나의 배열로 합칩니다.
                nestedItems = nestedListTokens.flatMap(listToken => buildListItems(listToken.items));
            }

            return {
                content: content.trim(),
                items: nestedItems,
            };
        });
    };

    for (const token of tokens) {
        let newBlocks = []; // 하나의 토큰에서 여러 블록이 생성될 수 있음

        switch (token.type) {
            case 'heading':
                const rawLevel = token.depth;
                const level = Math.min(Math.max(rawLevel, 1), 6);

                // 인라인 토큰이 있으면 직접 HTML로 변환, 없으면 텍스트 사용
                const headingText = token.tokens && token.tokens.length > 0
                    ? inlineTokensToHtml(token.tokens)
                    : token.text;

                newBlocks.push({
                    type: 'header',
                    data: {
                        text: headingText,
                        level: level
                    }
                });

                break;
            case 'paragraph':
                // paragraph 토큰 내의 모든 내부 토큰들을 순회합니다.
                // 이미지는 별도 블록으로 분리하고, 나머지 인라인 토큰은 직접 HTML로 변환합니다.
                let currentTokens = []; // 인라인 토큰 버퍼

                for (const innerToken of token.tokens || []) {
                    // 내부 토큰이 이미지인 경우
                    if (innerToken.type === 'image') {
                        // 1. 이미지 이전에 축적된 토큰이 있다면, 먼저 paragraph 블록으로 만듭니다.
                        if (currentTokens.length > 0) {
                            const html = inlineTokensToHtml(currentTokens).trim();
                            if (html) {
                                newBlocks.push({ type: 'paragraph', data: { text: html } });
                            }
                        }
                        currentTokens = []; // 토큰 버퍼 비우기

                        // 2. 이미지 블록을 별도로 추가합니다.
                        newBlocks.push({
                            type: 'image',
                            data: {
                                file: { url: innerToken.href },
                                caption: innerToken.text,
                                link: '', withBorder: false, withBackground: true, withoutGap: false
                            }
                        });
                    }
                    // 링크 안에 이미지가 있는 경우도 처리
                    else if (innerToken.type === 'link' && innerToken.tokens?.length === 1 && innerToken.tokens[0].type === 'image') {
                        if (currentTokens.length > 0) {
                            const html = inlineTokensToHtml(currentTokens).trim();
                            if (html) {
                                newBlocks.push({ type: 'paragraph', data: { text: html } });
                            }
                        }
                        currentTokens = [];
                        const imageToken = innerToken.tokens[0];
                        newBlocks.push({
                            type: 'image', data: { file: { url: imageToken.href }, caption: imageToken.text, link: innerToken.href, withBorder: false, withBackground: true, withoutGap: false }
                        });
                    }
                    else {
                        // 이미지가 아니면, 토큰을 버퍼에 추가합니다.
                        currentTokens.push(innerToken);
                    }
                }

                // 3. 루프가 끝난 후, 버퍼에 남은 토큰이 있다면 마지막으로 paragraph 블록을 만듭니다.
                if (currentTokens.length > 0) {
                    const html = inlineTokensToHtml(currentTokens).trim();
                    if (html) {
                        newBlocks.push({ type: 'paragraph', data: { text: html } });
                    }
                }
                break;
            case 'code':
                let highlighted_html = '';
                if (highlighter) {
                    try {
                        highlighted_html = highlighter.codeToHtml(token.text, {
                            lang: token.lang || 'plaintext',
                            theme: document.documentElement.classList.contains('dark') ? 'github-dark' : 'github-light'
                        });
                    } catch (e) { highlighted_html = `<pre><code>${escapeHtml(token.text)}</code></pre>`; }
                } else { highlighted_html = `<pre><code>${escapeHtml(token.text)}</code></pre>`; }

                newBlocks.push({
                    type: 'ShikiCodeTool',
                    data: { code: token.text, language: token.lang || 'plaintext', highlighted_html }
                });
                break;
            case 'list':
                newBlocks.push({
                    type: 'list',
                    data: {
                        style: token.ordered ? 'ordered' : 'unordered',
                        items: buildListItems(token.items),
                    }
                });
                break;
            case 'blockquote':
                // blockquote 내부 토큰이 있으면 직접 변환, 없으면 텍스트 파싱
                let quoteText = '';
                if (token.tokens && token.tokens.length > 0) {
                    // blockquote 내부에 paragraph 등이 있을 수 있음
                    const innerContent = token.tokens.map(t => {
                        if (t.type === 'paragraph' && t.tokens) {
                            return inlineTokensToHtml(t.tokens);
                        }
                        return t.text || '';
                    }).join('\n');
                    quoteText = innerContent;
                } else {
                    quoteText = convertToEditorJsTags(marked.parseInline(token.text || ''));
                }
                newBlocks.push({ type: 'quote', data: { text: quoteText, caption: '', alignment: 'left' } });
                break;
            case 'hr':
                newBlocks.push({ type: 'delimiter', data: {} });
                break;
            case 'table':
                // 셀 내용 변환 헬퍼
                const convertCell = (cell) => {
                    if (cell.tokens && cell.tokens.length > 0) {
                        return inlineTokensToHtml(cell.tokens);
                    }
                    return convertToEditorJsTags(marked.parseInline(cell.text || ''));
                };
                newBlocks.push({
                    type: 'table',
                    data: {
                        withHeadings: true,
                        content: [
                            token.header.map(convertCell),
                            ...token.rows.map(row => row.map(convertCell))
                        ]
                    }
                });
                break;
        }

        if (newBlocks.length > 0) {
            blocks.push(...newBlocks); // 생성된 블록(들)을 최종 배열에 추가
        }
    }
    return blocks;
}

/**
 * 주어진 텍스트가 Markdown인지 판별합니다.
 * - 수정사항: 단순히 문단이 많다고 해서 Markdown으로 판단하는 로직 삭제.
 * - 명확한 Markdown 문법(헤더, 리스트, 코드블럭 등)이 있을 때만 true를 반환합니다.
 */
export function validateMarkdownText(rawText = "") {
    // 너무 짧거나 유효하지 않은 텍스트는 조기 반환
    if (!rawText || typeof rawText !== 'string' || rawText.trim().length < 5) {
        return false;
    }

    // 1. 빠른 경로: 블록 레벨 마크다운 패턴이 한 줄이라도 시작 부분에 있으면 즉시 true 반환
    // 헤더(#), 숫자 리스트, 불릿 리스트, 인용구(>), 코드블럭(```), 테이블(|), 수평선(---)
    const blockPattern = /^(#+\s|\d+\.\s|\*\s|-\s|>\s|`{3}|\|.*\||---\s*$)/m;
    if (blockPattern.test(rawText)) {
        return true;
    }

    // 2. 점수 기반 추론: 텍스트 전체에서 마크다운 특징을 찾아 점수를 매김
    let score = 0;

    // 링크: [text](url) - 매우 강력한 신호 (개당 +2점)
    const links = rawText.match(/\[([^\]]+)\]\([^\)]+\)/g) || [];
    score += links.length * 2;

    // 볼드: **text** - 강력한 신호 (개당 +1점)
    const bolds = rawText.match(/\*\*([^\*]+)\*\*/g) || [];
    score += bolds.length;

    // 이탤릭: *text* 또는 _text_ - 비교적 약한 신호 (개당 +0.5점)
    const italics = rawText.match(/(?<!\*)\*([^\*]+)\*(?!\*)|_([^_]+)_/g) || [];
    score += italics.length * 0.5;

    // Setext 스타일 헤더 (밑줄 --- 또는 ===) - 강력한 신호 (개당 +2점)
    const setextHeaders = rawText.match(/^(.+)\n[=-]{3,}\s*$/gm) || [];
    score += setextHeaders.length * 2;

    /*
     * [삭제됨] 문단 수 기반 가산점 로직
     * 단순 텍스트 복사 시 오작동의 주범이므로 제거함.
     * const paragraphs = rawText.split(/\n\s*\n/).filter(p => p.trim() !== '').length;
     * if (paragraphs > 1) score += (paragraphs - 1);
     */

    // 임계값(Threshold) 설정: 점수가 2 이상이면 마크다운으로 간주
    return score >= 2;
}

export function convertMarkdownToBlocks(markdownText) {
    const normalized = markdownText.replace(/^ {0,3}```\t/gm, '```');
    const tokens = marked.lexer(normalized.trim(), { gfm: true });

    // 토큰을 Editor.js 블록으로 변환
    const blocks = tokensToEditorBlocks(tokens);

    return blocks;
}

export async function processMarkdownPasteEvent(editor, event) {
    const target = event.target;

    // 1. 이벤트 타겟이 'textarea'나 'input'이면 핸들러를 실행하지 않고 즉시 종료합니다.
    if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') {
        return;
    }

    // 2. 이벤트 타겟이 contenteditable이 아닌 요소(예: 버튼)에서 발생한 경우도 무시합니다.
    if (!target.isContentEditable) {
        return;
    }

    if (target.closest('.markdown-tool, .ce-code, .ce-rawtool')) {
        return;
    }

    const clipboardData = event.clipboardData || window.clipboardData;

    // [수정사항] 3. HTML 데이터가 존재하면(Rich Text 복사), 마크다운 핸들러를 실행하지 않고
    // Editor.js의 기본 붙여넣기 동작(HTML 처리)에 맡깁니다.
    // 이를 통해 이미지나 블록 구조가 깨지는 것을 방지합니다.
    const htmlData = clipboardData.getData('text/html');
    if (htmlData && /<[a-z][\s\S]*>/i.test(htmlData)) {
        return;
    }

    const pastedText = clipboardData.getData('text/plain');

    if (!validateMarkdownText(pastedText)) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();

    try {
        await editor.isReady;

        const newBlocks = convertMarkdownToBlocks(pastedText)

        if (newBlocks.length > 0) {
            const currentBlockIndex = editor.blocks.getCurrentBlockIndex();
            editor.blocks.insertMany(newBlocks, undefined, currentBlockIndex + 1);

            const currentBlock = editor.blocks.getBlockByIndex(currentBlockIndex);

            if (currentBlock && currentBlock.isEmpty) {
                editor.blocks.delete(currentBlockIndex);
                editor.caret.setToBlock(currentBlockIndex, 'start');
            } else {
                editor.caret.setToBlock(currentBlockIndex + 1, 'start');
            }
        }
    } catch (e) {
        console.error("Markdown paste processing failed:", e);
    }
}